import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Switch } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ColorSwatchPicker } from "../ColorSwatchPicker";
import { TEXT_COLOR_OPTIONS, TITLE_COLOR_OPTIONS } from "../printOptions";
import { FontPicker } from "../FontPicker";
import type { PrintLayoutFormValues } from "../printLayoutForm";
import { cn } from "@/lib/utils";
import { PanelShell } from "./PanelShell";

const SIZES = [9, 10, 11, 12];
const LINE_HEIGHTS = [1.15, 1.5, 2];

export function FontColorPanel({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const { t, i18n } = useTranslation("settings");
  const { watch, setValue, trigger, formState } = useFormContext<PrintLayoutFormValues>();
  const opts = { shouldDirty: true, shouldValidate: true };
  const num = (n: number) => n.toLocaleString(i18n.language, { minimumFractionDigits: 0 });

  return (
    <PanelShell title={t("printEditor.areas.body")}>
      <div className="flex flex-col gap-2">
        <Label id="print-font-label">{t("printEditor.body.font")}</Label>
        <FontPicker variant={variant} labelledBy="print-font-label" label={t("printEditor.body.font")} value={watch("fontId")} onChange={(id) => setValue("fontId", id, opts)} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="print-font-size">{t("printEditor.body.size")}</Label>
          <Select value={String(watch("fontSizePt"))} onValueChange={(v) => setValue("fontSizePt", Number(v), opts)}>
            <SelectTrigger id="print-font-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIZES.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s} pt
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="print-line-height">{t("printEditor.body.lineHeight")}</Label>
          <Select value={String(watch("lineHeight"))} onValueChange={(v) => setValue("lineHeight", Number(v) as 1.15 | 1.5 | 2, opts)}>
            <SelectTrigger id="print-line-height">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LINE_HEIGHTS.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {num(h)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <ColorSwatchPicker
        variant={variant}
        label={t("printEditor.body.textColor")}
        value={watch("textColor")}
        options={TEXT_COLOR_OPTIONS}
        onChange={(hex) => setValue("textColor", hex, opts)}
        onBlur={() => void trigger("textColor")}
        error={formState.errors.textColor?.message}
      />
      <ColorSwatchPicker
        variant={variant}
        label={t("printEditor.body.titleColor")}
        value={watch("titleColor")}
        options={TITLE_COLOR_OPTIONS}
        onChange={(hex) => setValue("titleColor", hex, opts)}
        onBlur={() => void trigger("titleColor")}
        error={formState.errors.titleColor?.message}
      />
      <p className="text-xs text-muted-foreground">{t("printEditor.body.contrastHint")}</p>

      <div
        className={cn(
          "flex items-start justify-between gap-3",
          variant === "mobile" && "min-h-14 items-center rounded-[14px] border px-3.5 py-2",
        )}
      >
        <div className="flex flex-col gap-0.5">
          <Label htmlFor="print-issuer" className="text-[13.5px]">
            {t("printEditor.body.issuer")}
          </Label>
          <p id="print-issuer-hint" className="text-xs text-muted-foreground">
            {t("printEditor.body.issuerHint")}
          </p>
        </div>
        <Switch
          id="print-issuer"
          aria-describedby="print-issuer-hint"
          checked={watch("showIssuerBlock")}
          onCheckedChange={(checked) => setValue("showIssuerBlock", checked, opts)}
        />
      </div>
      <p className="text-xs text-muted-foreground">{t("printEditor.body.mandatoryNote")}</p>
    </PanelShell>
  );
}
